import * as THREE from 'three';
import { A, D, DIRECTIONS, S, W, IDLE, WALK, RUN, SHIFT, Z, Q } from './utils';

export class CharacterControls {
    constructor(camera, domElement, initialPosition = [0, 1.7, 5]) {
        // Camera and controls
        this.camera = camera;
        this.domElement = domElement;
        
        // Character state
        this.toggleRun = false;
        this.currentAction = IDLE;
        this.keysPressed = {};
        
        // Movement vectors
        this.walkDirection = new THREE.Vector3();
        this.rotateAngle = new THREE.Vector3(0, 1, 0);
        this.rotateQuaternion = new THREE.Quaternion();
        
        // Character position
        this.position = new THREE.Vector3(...initialPosition);
        
        // Movement constants
        this.runVelocity = 20;
        this.walkVelocity = 8;
        this.rotationSpeed = 2;
        
        // Room bounds - more restricted to match cyberpunk office
        this.roomBounds = { minX: -10, maxX: 10, minZ: -7.5, maxZ: 7.5 };
        
        // Initialize camera position
        console.log('Setting camera to initial position:', initialPosition);
        camera.position.set(...initialPosition);
        
        // Collision detection properties
        this.collisionObjects = [];
        this.playerRadius = 0.8; // Increased collision radius of the player
        this.raycaster = new THREE.Raycaster();
        this.raycaster.near = 0.1;
        this.raycaster.far = 1.5; // Shorter distance for more precise collision
        
        // Debug flag
        this.debug = false;
    }
    
    // Method to update key state from outside
    updateKeys(keysPressed) {
        this.keysPressed = keysPressed;
        this.toggleRun = keysPressed[SHIFT] || false;
    }
    
    // Method to set collision objects from the scene
    setCollisionObjects(objects) {
        this.collisionObjects = objects;
        if (this.debug) {
            console.log('Collision objects set:', this.collisionObjects.length);
        }
    }
    
    // Improved collision checking for walls
    checkDirectionalCollision(position, direction) {
        // First check room bounds - hard boundaries
        const targetX = position.x + direction.x;
        const targetZ = position.z + direction.z;
        
        // Check if movement would place player outside bounds
        if (targetX < this.roomBounds.minX + this.playerRadius || 
            targetX > this.roomBounds.maxX - this.playerRadius || 
            targetZ < this.roomBounds.minZ + this.playerRadius || 
            targetZ > this.roomBounds.maxZ - this.playerRadius) {
            return true; // Collision with room boundary
        }
        
        // Check for collisions with specific objects if available
        if (!this.collisionObjects || this.collisionObjects.length === 0) {
            return false;
        }

        // Create rays in multiple directions to better detect collisions
        const angles = [0, Math.PI/6, -Math.PI/6, Math.PI/4, -Math.PI/4];
        const rayDirections = angles.map(angle => {
            return direction.clone()
                .normalize()
                .applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);
        });

        // Create rays at different heights to catch both low and high obstacles
        const heights = [0, 0.5, 1.0, 1.5];
        
        for (const rayDir of rayDirections) {
            for (const height of heights) {
                const rayOrigin = position.clone();
                rayOrigin.y += height;
                
                this.raycaster.set(rayOrigin, rayDir);
                const intersects = this.raycaster.intersectObjects(this.collisionObjects, true);
                
                // If there's an intersection closer than our movement distance + player radius, we have a collision
                const moveDist = direction.length();
                if (intersects.length > 0 && intersects[0].distance < moveDist + this.playerRadius) {
                    if (this.debug) {
                        console.log('Collision detected:', {
                            distance: intersects[0].distance,
                            moveDist: moveDist,
                            height: height,
                            angle: Math.atan2(rayDir.z, rayDir.x)
                        });
                    }
                    return true;
                }
            }
        }
        
        return false;
    }
    
    // Check for collisions in a more accurate way
    checkCollisions(position, moveX, moveZ) {
        const playerPosition = new THREE.Vector3(position.x, position.y, position.z);
        
        // Check room boundaries directly
        const targetX = position.x + moveX;
        const targetZ = position.z + moveZ;
        
        // Hard boundaries based on room dimensions
        const xAllowed = targetX >= this.roomBounds.minX + this.playerRadius && 
                         targetX <= this.roomBounds.maxX - this.playerRadius;
        const zAllowed = targetZ >= this.roomBounds.minZ + this.playerRadius && 
                         targetZ <= this.roomBounds.maxZ - this.playerRadius;
        
        // Check movement along X axis first (if allowed by boundaries)
        const xMove = xAllowed ? moveX : 0;
        
        const xCollision = xAllowed ? this.checkDirectionalCollision(
            playerPosition, 
            new THREE.Vector3(moveX, 0, 0)
        ) : true;
        
        // Then check Z axis movement from the new X position
        const zMove = zAllowed ? moveZ : 0;
        
        const zCollision = zAllowed ? this.checkDirectionalCollision(
            new THREE.Vector3(playerPosition.x + (xCollision ? 0 : xMove), playerPosition.y, playerPosition.z), 
            new THREE.Vector3(0, 0, moveZ)
        ) : true;
        
        // Return the allowed position and collision status
        return {
            x: position.x + (xCollision ? 0 : xMove),
            z: position.z + (zCollision ? 0 : zMove),
            collided: xCollision || zCollision || !xAllowed || !zAllowed
        };
    }
    
    update(delta) {
        // Check if any direction key is pressed
        const directionPressed = DIRECTIONS.some(key => this.keysPressed[key] === true);
        
        if (this.debug) {
            console.log('Direction pressed:', directionPressed);
            console.log('Current keys:', this.keysPressed);
        }
        
        // Determine movement state
        let action = IDLE;
        if (directionPressed) {
            action = this.toggleRun ? RUN : WALK;
        }
        
        // Update current action if needed
        if (this.currentAction !== action) {
            this.currentAction = action;
        }
        
        // Track if we collided during this update
        let isColliding = false;
        
        // Handle movement if walking or running
        if (this.currentAction === RUN || this.currentAction === WALK) {
            // Set velocity based on action
            const velocity = this.currentAction === RUN ? this.runVelocity : this.walkVelocity;
            
            // Get forward direction from camera
            const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
            forward.y = 0;
            forward.normalize();
            
            // Get right direction from camera
            const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
            right.y = 0;
            right.normalize();
            
            // Calculate movement direction based on keys
            const moveDirection = new THREE.Vector3(0, 0, 0);
            
            if (this.keysPressed[W] || this.keysPressed[Z]) {
                moveDirection.add(forward);
            }
            if (this.keysPressed[S]) {
                moveDirection.sub(forward);
            }
            if (this.keysPressed[A] || this.keysPressed[Q]) {
                moveDirection.sub(right);
            }
            if (this.keysPressed[D]) {
                moveDirection.add(right);
            }
            
            // Store for debug output
            this.walkDirection.copy(moveDirection);
            
            // Normalize movement direction if it exists
            if (moveDirection.length() > 0) {
                moveDirection.normalize();
            }
            
            // Calculate movement
            const moveX = moveDirection.x * velocity * delta;
            const moveZ = moveDirection.z * velocity * delta;
            
            if (this.debug) {
                console.log('Move X:', moveX, 'Move Z:', moveZ);
            }
            
            // Check for collisions and get allowed position
            const allowedPosition = this.checkCollisions(this.camera.position, moveX, moveZ);
            
            // Update collision state
            isColliding = allowedPosition.collided;
            
            // Update camera position with collision detection
            this.camera.position.x = allowedPosition.x;
            this.camera.position.z = allowedPosition.z;
            
            // Update position for tracking
            this.position.copy(this.camera.position);
        }
        
        // Return current state for React component
        return {
            position: {
                x: parseFloat(this.camera.position.x.toFixed(2)),
                y: parseFloat(this.camera.position.y.toFixed(2)),
                z: parseFloat(this.camera.position.z.toFixed(2))
            },
            action: this.currentAction,
            direction: {
                x: parseFloat(this.walkDirection.x.toFixed(2)),
                z: parseFloat(this.walkDirection.z.toFixed(2))
            },
            keysPressed: { ...this.keysPressed },
            isColliding: isColliding
        };
    }
    
    dispose() {
        // Clean up
        this.collisionObjects = null;
    }
} 